import React from "react";
import {
    Area,
    AreaChart,
    Brush,
    CartesianGrid,
    Legend,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import styled from "styled-components";
import { Stack } from "@mui/material";
import { format, isValid } from "date-fns";
import TimeSplitter from "src/utils/time-splitter";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import JointChartDataSkeletonHelper from "src/components/joint-chart-data-skeleton-helper";
import ChartInfoBox from "src/scenes/overview/overview-components/chart-info-box";
import ZeroAccuracyPlaceholder from "src/scenes/overview/overview-components/zero-accuracy-placeholder";

const TooltipContainer = styled.div`
    background-color: ${({ theme }) => theme.palette.background.tooltip};
    padding: 0.5rem;
    min-width: 10rem;
    align-items: center;
    flex-direction: column;
    border-radius: 6px;
`;

const TooltipTitle = styled.p`
    margin-top: 0;
    margin-bottom: 0.5rem;
`;

const TooltipContent = styled.p`
    font-size: 12px;
    margin: 0;
    color: ${({ color }) => color};
`;

const CustomTooltip = ({ active, payload, isExpanded }) => {
    if (active && payload && payload.length) {
        return (
            <TooltipContainer role="status" aria-live="assertive">
                <TooltipTitle>
                    {isValid(new Date(payload[0].payload.datetime)) &&
                        `${format(new Date(payload[0].payload.datetime), "EEE d MMM")}`}
                </TooltipTitle>
                {payload.map((entry) => {
                    return (
                        <Stack direction="row" spacing={0.5}>
                            <TooltipContent color={entry.fill}>{entry.name}</TooltipContent>
                            <TooltipContent>
                                {TimeSplitter(entry.value, "variable-accuracy")}{" "}
                                {TimeSplitter(entry.value, "text")} (per user)
                            </TooltipContent>
                        </Stack>
                    );
                })}
                {isExpanded && (
                    <Stack
                        direction="column"
                        mt={1}
                        style={{ width: "100%", backgroundColor: "#303030", borderRadius: "8px" }}
                    >
                        <TooltipContent style={{ fontWeight: 300, margin: "8px" }}>
                            Click for details
                        </TooltipContent>
                    </Stack>
                )}
            </TooltipContainer>
        );
    }
    return null;
};
const InvestmentMainChart = ({ data, status, isExpanded, accuracy }) => {
    if (!data) return <NoDataPlaceholder variant="overview" />;

    if (
        status === "STREAMING" &&
        !["code", "collaboration", "balance"].every((key) => Object.keys(data).includes(key))
    )
        return <JointChartDataSkeletonHelper />;
    if (
        status === "COMPLETE" &&
        !["code", "collaboration", "balance"].every((key) => Object.keys(data).includes(key))
    )
        return <NoDataPlaceholder variant="overview" />;

    if (accuracy && accuracy.level === "zero")
        return <ZeroAccuracyPlaceholder dependencies={accuracy.dependencies} />;

    const [activeSeries, setActiveSeries] = React.useState([]);
    const [clickedPoint, setClickedPoint] = React.useState(null);

    const handleClick = (e) => {
        setClickedPoint(e);
    };
    const flattenInvestmentData = () => {
        const { code: codeData, collaboration: collaborationData, balance: balanceData } = data;
        const flattenedDataMap = {};
        (codeData || []).forEach((item) => {
            const { datetime, code, avgPerUserDay, details } = item;

            if (!flattenedDataMap[datetime]) {
                flattenedDataMap[datetime] = { datetime };
            }
            flattenedDataMap[datetime].code = code;
            flattenedDataMap[datetime].codeDetails = details ? [...details] : [];
            flattenedDataMap[datetime].avgCodePerUserDay = avgPerUserDay;
        });

        (collaborationData || []).forEach((item) => {
            const { datetime, collaboration, avgPerUserDay, details } = item;
            if (!flattenedDataMap[datetime]) {
                flattenedDataMap[datetime] = { datetime };
            }
            flattenedDataMap[datetime].collaboration = collaboration;
            flattenedDataMap[datetime].collabDetails = details ? [...details] : [];
            flattenedDataMap[datetime].avgCollabPerUserDay = avgPerUserDay;
        });

        (balanceData || []).forEach((item) => {
            const {
                datetime,
                oooBreak,
                ooo,
                break: breakTime,
                avgOooBreakPerUserDay,
                avgOooPerUserDay,
                avgBreakPerUserDay,
                details,
            } = item;
            if (!flattenedDataMap[datetime]) {
                flattenedDataMap[datetime] = { datetime };
            }
            flattenedDataMap[datetime].oooBreak = oooBreak;
            flattenedDataMap[datetime].ooo = ooo;
            flattenedDataMap[datetime].break = breakTime;
            flattenedDataMap[datetime].avgOooBreakPerUserDay = avgOooBreakPerUserDay;
            flattenedDataMap[datetime].avgOooPerUserDay = avgOooPerUserDay;
            flattenedDataMap[datetime].avgBreakPerUserDay = avgBreakPerUserDay;
            flattenedDataMap[datetime].balanceDetails = details ? [...details] : [];
        });

        return Object.values(flattenedDataMap);
    };

    const flatInvestmentData = flattenInvestmentData();

    const handleLegendClick = (dataKey) => {
        if (activeSeries.includes(dataKey)) {
            setActiveSeries(activeSeries.filter((el) => el !== dataKey));
        } else {
            setActiveSeries((prev) => [...prev, dataKey]);
        }
    };

    return (
        <div style={{ width: "100%" }}>
            <ResponsiveContainer width="100%" height={isExpanded ? 400 : 200}>
                <AreaChart
                    accessibilityLayer
                    data={flatInvestmentData.map(
                        ({
                            datetime,
                            collaboration,
                            code,
                            break: breakTime,
                            oooBreak,
                            ooo,
                            codeDetails,
                            collabDetails,
                            balanceDetails,
                        }) => ({
                            datetime,
                            collaboration,
                            code,
                            codeDetails,
                            breakTime,
                            oooBreak,
                            ooo,
                            collabDetails,
                            balanceDetails,
                            balanceSum: ooo + oooBreak + breakTime,
                            date:
                                isValid(new Date(datetime)) && format(new Date(datetime), "d MMM"),
                        })
                    )}
                    syncId={isExpanded ? null : "mainAnalyticsSync"}
                    syncMethod="value"
                    onClick={(e) => handleClick(e)}
                    margin={{
                        top: 25,
                        right: 50,
                        left: 0,
                        bottom: 15,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis
                        dataKey="date"
                        tick={{
                            fill: "#EAF0F4",
                            opacity: "0.3",
                            fontSize: "11px",
                            fontWeight: "300",
                        }}
                    />
                    <YAxis
                        tick={{
                            fill: "#EAF0F4",
                            opacity: "0.3",
                            fontSize: "11px",
                            fontWeight: "300",
                        }}
                        tickFormatter={(tick) => TimeSplitter(tick, "minified")}
                    />
                    <Tooltip
                        cursor={{ opacity: "0.2" }}
                        content={<CustomTooltip isExpanded={isExpanded} />}
                    />
                    <Area
                        dataKey="code"
                        stackId="a"
                        fill="#8884d8"
                        stroke="#8884d8"
                        name="Code Days"
                        hide={activeSeries.includes("code")}
                    />
                    <Area
                        dataKey="collaboration"
                        stackId="b"
                        fill="#82ca9d"
                        stroke="#82ca9d"
                        style={{ opacity: 0.8 }}
                        name="Collaboration Days"
                        hide={activeSeries.includes("collaboration")}
                    />
                    <Area
                        dataKey="balanceSum"
                        stackId="c"
                        fill="#ffc658"
                        stroke="#ffc658"
                        style={{ opacity: 0.6 }}
                        name="Balance Days"
                        hide={activeSeries.includes("balanceSum")}
                    />
                    {!isExpanded && (
                        <Brush
                            height={15}
                            stroke="#333"
                            fill="#444"
                            travellerWidth={10}
                            travellerStroke="#555"
                            travellerFill="#666"
                        />
                    )}
                    {clickedPoint !== null && isExpanded && (
                        <ReferenceLine x={clickedPoint.activeLabel} stroke="#5ac36b" />
                    )}
                    {isExpanded && (
                        <Legend
                            verticalAlign="bottom"
                            height={22}
                            wrapperStyle={{ fontSize: "11px", bottom: "6px" }}
                            onClick={(props) => handleLegendClick(props.dataKey)}
                        />
                    )}
                </AreaChart>
            </ResponsiveContainer>
            {isExpanded && clickedPoint && clickedPoint.activePayload && (
                <ChartInfoBox clickedPoint={clickedPoint} type="investment" />
            )}
        </div>
    );
};

export default InvestmentMainChart;
